import * as THREE from 'three';
import type { MapDef, Zone } from '@somnhold/sim';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { veinTexture } from './ascuas.ts';
import { WORLD_PER_HEX } from './coords.ts';
import type { TerrainBuild } from './terrain.ts';

/*
 * Obsidiana (incursion level III): the clearing turned to black volcanic glass, the glass of the Burn.
 *
 * - The skin: the ground is repainted near-black, cut into sharp facets (each Voronoi plate of the shared
 *   vein map tilts its normal its own way, so the key light catches some facets and not others) with
 *   thin pale seams between them and rare violet or red glints running along the seams; all in the
 *   ground's own shader, no extra draw.
 * - Props: spires and clusters of glass beyond the clearing (one instanced draw), the woods glassed over,
 *   a cold violet-black dusk and glass dust hanging in the air (one Points draw).
 * - Cracking glass (`zone.kind === 'glass'`): while `warn` counts down, fractures spread from the hero's
 *   feet and the danger circle blinks faster and redder («move!»); once it gives way (`warn` -1) a pit with
 *   jagged edges over a dark void, splinters standing on its rim.
 * - Shards (`'shards'`): a patch of glittering splinters, a few standing up, over a dull red stain.
 * The cracks, pits and shards share one dynamic mesh; the standing splinters one instanced mesh.
 */

const K = WORLD_PER_HEX / 1000;
const clamp01 = (x: number): number => (x < 0 ? 0 : x > 1 ? 1 : x);
const lum = (r: number, g: number, b: number): number => 0.2126 * r + 0.7152 * g + 0.0722 * b;

/* ---------- the ground skin ---------- */

const OB_PARS = /* glsl */ `
uniform sampler2D tVein;
uniform float uObTime;
uniform float uObClear;
varying vec3 vObW;`;

/** Each facet of glass leans its own way: the key light catches some, misses others. */
const OB_NORMAL = /* glsl */ `
{
  vec4 fa = texture2D(tVein, vObW.xz * 0.11);
  float fid = fa.g;
  vec3 tilt = vec3(fid - 0.5, 0.0, fract(fid * 7.31 + 0.13) - 0.5) * 0.32;
  normal = normalize(normal + (viewMatrix * vec4(tilt, 0.0)).xyz);
}`;

/** Facets with pale seams and rare violet (now and then red) glints running along the seams. */
const OB_FRAG = /* glsl */ `
{
  vec2 p = vObW.xz;
  vec4 a = texture2D(tVein, p * 0.11);
  vec4 b = texture2D(tVein, p * 0.27 + vec2(0.37, 0.61));
  float outside = smoothstep(uObClear * 0.85, uObClear * 1.5, length(p));
  float e1 = 1.0 - smoothstep(0.0, 0.02, a.r);
  float e2 = (1.0 - smoothstep(0.0, 0.014, b.r)) * mix(0.55, 0.3, outside);
  float edge = max(e1, e2);
  diffuseColor.rgb *= 0.75 + 0.5 * a.g;
  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.36, 0.34, 0.46), edge * 0.4);
  float tw = pow(max(0.0, sin(uObTime * (0.3 + a.g * 0.5) + a.g * 47.0 + b.g * 13.0)), 28.0);
  vec3 glintC = mix(vec3(0.62, 0.42, 1.0), vec3(1.0, 0.31, 0.23), step(0.86, b.g));
  totalEmissiveRadiance += glintC * edge * (0.035 + 0.7 * tw);
}`;

export const obsidianMaterial = (src: THREE.MeshStandardMaterial, uniforms: Record<string, THREE.IUniform>): THREE.MeshStandardMaterial => {
  const m = src.clone();
  m.roughness = 0.42;
  m.metalness = 0.15;
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vObW;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvObW = (modelMatrix * vec4(transformed, 1.0)).xyz;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${OB_PARS}`)
      .replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>\n${OB_NORMAL}`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n${OB_FRAG}`);
  };
  m.customProgramCacheKey = () => 'somnhold-obsidian-ground';
  m.needsUpdate = true;
  return m;
};

/** Repaints a vertex-coloured mesh in glass: the old light and shade survive, the hue goes to violet-black. */
const glassify = (geo: THREE.BufferGeometry, dark: THREE.Color, light: THREE.Color, lo: number, hi: number): void => {
  const col = geo.getAttribute('color') as THREE.BufferAttribute | undefined;
  if (!col) return;
  const c = new THREE.Color();
  for (let i = 0; i < col.count; i++) {
    const t = clamp01((lum(col.getX(i), col.getY(i), col.getZ(i)) - lo) / (hi - lo));
    c.copy(dark).lerp(light, t);
    col.setXYZ(i, c.r, c.g, c.b);
  }
  col.needsUpdate = true;
};

const GLASS = {
  tileDark: new THREE.Color('#09080d'),
  tileLight: new THREE.Color('#2f2c3a'),
  groundDark: new THREE.Color('#060509'),
  groundLight: new THREE.Color('#1b1922'),
  // The paths stay a little lighter and rougher (ground glass, trodden): the way still reads.
  roadDark: new THREE.Color('#1a1820'),
  roadLight: new THREE.Color('#4a4654'),
  glassy: new THREE.Color('#0d0b12'),
  sheen: new THREE.Color('#2c2838'),
};

/* ---------- glass dust ---------- */

const DUST_VERT = /* glsl */ `
attribute vec4 aSeed;
uniform float uTime;
uniform float uPx;
varying float vAlpha;
varying float vHue;
void main() {
  float t = uTime * (0.25 + aSeed.w * 0.25) + aSeed.z * 31.0;
  float life = fract(t * 0.05);
  vec3 p = vec3(aSeed.x + sin(t * 0.4 + aSeed.z * 6.28) * 0.6, 0.2 + aSeed.w * 2.6 + sin(t * 0.3) * 0.3 - life * 0.6, aSeed.y + cos(t * 0.33 + aSeed.w * 6.28) * 0.6);
  vec4 mv = modelViewMatrix * vec4(p, 1.0);
  float tw = pow(max(0.0, sin(uTime * (1.2 + aSeed.z * 2.0) + aSeed.w * 40.0)), 6.0);
  vAlpha = smoothstep(0.0, 0.15, life) * (1.0 - smoothstep(0.7, 1.0, life)) * (0.25 + 0.75 * tw);
  vHue = aSeed.z;
  gl_PointSize = 0.11 * (0.5 + aSeed.w) * uPx / max(0.5, -mv.z);
  gl_Position = projectionMatrix * mv;
}`;

const DUST_FRAG = /* glsl */ `
varying float vAlpha;
varying float vHue;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  // A four-pointed glint, not a soft dot.
  float star = max(1.0 - smoothstep(0.0, 0.08, abs(c.x)) , 1.0 - smoothstep(0.0, 0.08, abs(c.y))) * (1.0 - smoothstep(0.1, 0.5, length(c)));
  float core = 1.0 - smoothstep(0.0, 0.18, length(c));
  vec3 col = mix(vec3(0.78, 0.7, 1.0), vec3(1.0, 0.45, 0.4), step(0.9, vHue));
  gl_FragColor = vec4(col, max(star, core) * vAlpha);
}`;

/* ---------- cracks, pits and shards ---------- */

const DECAL_VERT = /* glsl */ `
attribute vec2 aLocal;
attribute vec2 aSeed;
varying vec2 vL;
varying vec2 vS;
void main() {
  vL = aLocal;
  vS = aSeed;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

/**
 * vS.x: a seed per zone; vS.y: what it is. 0..1 the glass cracking (how far the countdown has run),
 * 2..3 the pit (2 + how much of it is left), 3..4 shards (3 + how much is left).
 */
const GLASS_FRAG = /* glsl */ `
uniform sampler2D tVein;
uniform float uTime;
varying vec2 vL;
varying vec2 vS;
float hash(float x) { return fract(sin(x * 127.1 + vS.x * 311.7) * 43758.5453); }
void main() {
  float d = length(vL);
  float ang = atan(vL.y, vL.x);
  float st = vS.y;
  vec3 col = vec3(0.0);
  float a = 0.0;
  if (st < 1.5) {
    // Cracking: radial fractures grow out of the feet, rings of fracture follow, the circle blinks.
    float p = clamp(st, 0.0, 1.0);
    float jit = (texture2D(tVein, vec2(d * 0.35 + vS.x, vS.x * 0.7)).b - 0.5) * 0.9;
    float rad = abs(sin((ang + jit * 0.35 + vS.x * 6.2831) * 3.5)) * max(d, 0.05);
    float reach = 0.15 + p * 0.95;
    float grow = 1.0 - smoothstep(reach - 0.08, reach, d);
    float line = (1.0 - smoothstep(0.0, 0.018 + 0.03 * p, rad)) * grow;
    vec4 v = texture2D(tVein, vL * 0.55 + vS.xx * 3.1);
    float web = (1.0 - smoothstep(0.0, 0.035, v.r)) * (1.0 - smoothstep(reach * 0.7 - 0.1, reach * 0.7, d)) * smoothstep(0.25, 0.6, p);
    float arc1 = exp(-pow((d - 0.42) / 0.018, 2.0)) * step(0.45, fract(ang * 0.477 + vS.x * 3.0)) * smoothstep(0.35, 0.55, p);
    float arc2 = exp(-pow((d - 0.76) / 0.02, 2.0)) * step(0.4, fract(ang * 0.636 + vS.x * 5.0)) * smoothstep(0.65, 0.85, p);
    float frac = max(max(line, web * 0.75), max(arc1, arc2));
    float blink = 0.55 + 0.45 * sin(uTime * (6.0 + 22.0 * p));
    float circle = exp(-pow((d - 1.0) / 0.035, 2.0)) * (0.5 + 0.5 * blink);
    float dash = step(0.5, fract(ang * 1.9099 + uTime * 0.8)) * exp(-pow((d - 1.08) / 0.022, 2.0)) * 0.7 * p;
    float shade = (1.0 - smoothstep(0.95, 1.0, d)) * (0.18 + 0.3 * p);
    float hotC = p * p;
    vec3 white = vec3(1.0, 0.96, 1.0);
    vec3 red = vec3(1.0, 0.24, 0.16);
    col = mix(white, red, hotC * 0.6) * frac * (1.1 + 0.6 * p * blink);
    col += red * (circle * (0.6 + 0.6 * p) + dash);
    col += vec3(0.5, 0.06, 0.05) * (1.0 - smoothstep(0.0, 0.3 + 0.4 * p, d)) * p * 0.6;
    a = max(max(frac, circle), max(shade, dash));
    col = mix(vec3(0.0), col, clamp(max(frac, circle + dash) * 1.5, 0.0, 1.0)) + vec3(0.02, 0.0, 0.0) * shade;
  } else if (st < 3.0) {
    // The pit: a jagged hole over a dark void, a bright broken rim, short cracks running off it.
    float f = clamp(st - 2.0, 0.0, 1.0);
    float seg = ang / 6.2831 * 11.0 + vS.x * 11.0;
    float i0 = floor(seg);
    float t = fract(seg);
    float e = mix(0.66 + 0.2 * hash(i0), 0.66 + 0.2 * hash(i0 + 1.0), t);
    float inside = 1.0 - smoothstep(e - 0.015, e, d);
    float depth = clamp(d / e, 0.0, 1.0);
    vec3 voidC = mix(vec3(0.0, 0.0, 0.004), vec3(0.06, 0.012, 0.09), depth * depth) ;
    voidC += vec3(0.18, 0.04, 0.3) * pow(1.0 - depth, 6.0) * (0.6 + 0.4 * sin(uTime * 1.3 + vS.x * 9.0)) * 0.35;
    float rim = exp(-pow((d - e) / 0.02, 2.0));
    float jit = (texture2D(tVein, vec2(ang * 0.3 + vS.x, 0.4)).b - 0.5);
    float rad = abs(sin((ang + jit * 0.3 + vS.x * 4.0) * 5.5)) * d;
    float cracks = (1.0 - smoothstep(0.0, 0.02, rad)) * smoothstep(e, e + 0.02, d) * (1.0 - smoothstep(e + 0.1, e + 0.42, d));
    col = mix(vec3(0.0), voidC, inside) + vec3(0.85, 0.8, 1.0) * rim * 0.9 + vec3(0.6, 0.5, 0.85) * cracks * 0.7;
    col += vec3(0.6, 0.08, 0.06) * rim * 0.4;
    a = max(inside, max(rim, cracks)) * smoothstep(0.0, 0.25, f);
  } else {
    // Shards: splinters strewn on a dull red stain, glittering.
    float f = clamp(st - 3.0, 0.0, 1.0);
    vec2 g = vL * 6.0;
    vec2 cell = floor(g);
    vec2 fr = fract(g) - 0.5;
    float h = fract(sin(dot(cell, vec2(12.9898, 78.233)) + vS.x * 91.0) * 43758.5453);
    float h2 = fract(h * 13.7);
    float ca = cos(h * 6.2831), sa = sin(h * 6.2831);
    vec2 q = mat2(ca, -sa, sa, ca) * (fr - (vec2(h, h2) - 0.5) * 0.3);
    float sliver = (1.0 - smoothstep(0.02, 0.05, abs(q.y) + abs(q.x) * 0.12)) * (1.0 - smoothstep(0.28, 0.34, abs(q.x))) * step(0.35, h2);
    float inside = 1.0 - smoothstep(0.85, 1.0, d);
    float tw = pow(max(0.0, sin(uTime * (2.0 + h * 3.0) + h2 * 40.0)), 12.0);
    vec3 stain = vec3(0.07, 0.02, 0.06);
    vec3 glass = mix(vec3(0.05, 0.045, 0.08), vec3(1.0, 0.95, 1.0), tw * 0.9) + vec3(0.25, 0.18, 0.45) * (1.0 - smoothstep(0.0, 0.3, abs(q.x)));
    col = mix(stain, glass, sliver) ;
    float rim = exp(-pow((d - 0.95) / 0.03, 2.0)) * 0.45 * step(0.5, fract(atan(vL.y, vL.x) * 1.9099));
    col += vec3(1.0, 0.31, 0.23) * rim;
    a = max(inside * mix(0.4, 1.0, sliver), rim) * smoothstep(0.0, 0.3, f);
  }
  gl_FragColor = vec4(col, clamp(a, 0.0, 1.0));
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

interface Draped {
  pos: Float32Array;
  local: Float32Array;
  index: number[];
}

const RINGS = 9;
const SEGS = 40;
const VERTS = 1 + RINGS * SEGS;
const SPREAD = 0.22;

/** A disc (out to `reach` × radius) laid over the ground, in world space; local coordinates are in radii. */
const drape = (cx: number, cz: number, r: number, reach: number, ground0: (x: number, z: number) => number, lift: number): Draped => {
  const ground = (x: number, z: number): number =>
    Math.max(ground0(x, z), ground0(x + SPREAD, z), ground0(x - SPREAD, z), ground0(x, z + SPREAD), ground0(x, z - SPREAD));
  const pos: number[] = [cx, ground(cx, cz) + lift, cz];
  const local: number[] = [0, 0];
  const index: number[] = [];
  for (let i = 1; i <= RINGS; i++) {
    const k = (i / RINGS) * reach;
    for (let s = 0; s < SEGS; s++) {
      const a = (s / SEGS) * Math.PI * 2;
      const lx = Math.cos(a) * k, lz = Math.sin(a) * k;
      const x = cx + lx * r, z = cz + lz * r;
      pos.push(x, ground(x, z) + lift, z);
      local.push(lx, lz);
    }
  }
  for (let s = 0; s < SEGS; s++) index.push(0, 1 + ((s + 1) % SEGS), 1 + s);
  for (let i = 1; i < RINGS; i++) {
    const a0 = 1 + (i - 1) * SEGS, b0 = 1 + i * SEGS;
    for (let s = 0; s < SEGS; s++) {
      const s1 = (s + 1) % SEGS;
      index.push(a0 + s, a0 + s1, b0 + s, a0 + s1, b0 + s1, b0 + s);
    }
  }
  return { pos: new Float32Array(pos), local: new Float32Array(local), index };
};

const MAX_MARKS = 28;
const MAX_SPLINTERS = 360;

/** The state a glass or shards zone is drawn in (see GLASS_FRAG). */
export const glassState = (z: Zone, maxWarn: number): number => {
  if (z.kind === 'shards') return 3 + Math.min(0.999, Math.max(0, z.ticks / 18));
  const w = z.warn ?? 0;
  if (w > 0) return clamp01(1 - (w - 1) / Math.max(1, maxWarn));
  return 2 + Math.min(0.999, Math.max(0, z.ticks / 24));
};

/** Splinter geometry: a thin three-sided blade, its point up. */
const splinterGeo = (): THREE.BufferGeometry => {
  const a = new THREE.ConeGeometry(0.035, 0.24, 3);
  a.translate(0, 0.1, 0);
  const b = new THREE.ConeGeometry(0.022, 0.15, 3);
  b.translate(0.04, 0.06, 0.01);
  b.rotateZ(-0.35);
  return mergeGeometries([a, b]) as THREE.BufferGeometry;
};

/** A spire of glass: a long faceted blade and two smaller ones leaning off its foot. */
const spireGeo = (): THREE.BufferGeometry => {
  const parts: THREE.BufferGeometry[] = [];
  const blade = (r: number, h: number, x: number, z: number, rx: number, rz: number): void => {
    const p = new THREE.CylinderGeometry(0, r, h, 5, 1);
    p.translate(0, h / 2, 0);
    p.rotateX(rx);
    p.rotateZ(rz);
    p.translate(x, 0, z);
    parts.push(p.toNonIndexed());
  };
  blade(0.16, 1.0, 0, 0, 0.05, -0.08);
  blade(0.09, 0.55, 0.12, 0.06, 0.25, -0.5);
  blade(0.07, 0.4, -0.1, -0.05, -0.3, 0.45);
  const g = mergeGeometries(parts) as THREE.BufferGeometry;
  g.computeVertexNormals();
  return g;
};

export interface ObsidianSky {
  hemi: THREE.HemisphereLight;
  key: THREE.DirectionalLight;
  rim: THREE.DirectionalLight;
}

export class Obsidian {
  readonly group = new THREE.Group();
  private uniforms = { tVein: { value: veinTexture() }, uTime: { value: 0 } };
  private groundUniforms = { tVein: this.uniforms.tVein, uObTime: this.uniforms.uTime, uObClear: { value: 5 } };
  private dust: THREE.Points;
  private dustU = { uTime: this.uniforms.uTime, uPx: { value: 400 } };
  private marks: THREE.Mesh;
  private markKey = '';
  private markState: THREE.BufferAttribute;
  private list: Zone[] = [];
  private drapes = new Map<number, Draped>();
  private warnMax = new Map<number, number>();
  private splinters: THREE.InstancedMesh;
  private splinterKey = '';
  private spires: THREE.InstancedMesh | null = null;

  constructor(
    private map: MapDef,
    private ground: (x: number, z: number) => number,
    private low: boolean,
    /** Where the dust hangs (world x, z); by default all over the board. The deep crypt passes its glass floor. */
    spots?: readonly (readonly [number, number])[],
  ) {
    this.group.name = 'obsidian';
    this.groundUniforms.uObClear.value = (map.bastion + 0.5) * WORLD_PER_HEX;
    const n = spots ? Math.min(low ? 80 : 180, spots.length * 2) : low ? 140 : 320;
    const seeds = new Float32Array(n * 4);
    let k = 0x7f4a7c15;
    const rnd = (): number => {
      k = (Math.imul(k ^ (k >>> 15), 2246822519) + 0x9e3779b9) >>> 0;
      return k / 4294967296;
    };
    const R = (map.radius - 1) * WORLD_PER_HEX;
    for (let i = 0; i < n; i++) {
      if (spots && spots.length > 0) {
        const sp = spots[Math.floor(rnd() * spots.length)] as readonly [number, number];
        seeds.set([sp[0] + (rnd() - 0.5) * 1.2, sp[1] + (rnd() - 0.5) * 1.2, rnd(), rnd()], i * 4);
        continue;
      }
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(0.02 + rnd() * 0.98) * R;
      seeds.set([Math.cos(a) * d, Math.sin(a) * d, rnd(), rnd()], i * 4);
    }
    const dg = new THREE.BufferGeometry();
    dg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    dg.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 4));
    this.dust = new THREE.Points(dg, new THREE.ShaderMaterial({ uniforms: this.dustU, vertexShader: DUST_VERT, fragmentShader: DUST_FRAG, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    this.dust.frustumCulled = false;
    this.dust.renderOrder = 8;
    const px = new THREE.Vector2();
    this.dust.onBeforeRender = (renderer) => {
      this.dustU.uPx.value = renderer.getDrawingBufferSize(px).y * 0.5;
    };
    const mg = new THREE.BufferGeometry();
    mg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MAX_MARKS * VERTS * 3), 3).setUsage(THREE.DynamicDrawUsage));
    mg.setAttribute('aLocal', new THREE.BufferAttribute(new Float32Array(MAX_MARKS * VERTS * 2), 2).setUsage(THREE.DynamicDrawUsage));
    this.markState = new THREE.BufferAttribute(new Float32Array(MAX_MARKS * VERTS * 2), 2).setUsage(THREE.DynamicDrawUsage);
    mg.setAttribute('aSeed', this.markState);
    this.marks = new THREE.Mesh(
      mg,
      new THREE.ShaderMaterial({
        uniforms: this.uniforms,
        vertexShader: DECAL_VERT,
        fragmentShader: GLASS_FRAG,
        transparent: true,
        depthWrite: false,
        polygonOffset: true,
        polygonOffsetFactor: -4,
        polygonOffsetUnits: -8,
      }),
    );
    this.marks.frustumCulled = false;
    this.marks.renderOrder = 2;
    this.marks.visible = false;
    this.splinters = new THREE.InstancedMesh(
      splinterGeo(),
      new THREE.MeshStandardMaterial({ color: '#100d16', emissive: '#3a2466', emissiveIntensity: 0.5, roughness: 0.1, metalness: 0.45, flatShading: true }),
      MAX_SPLINTERS,
    );
    this.splinters.count = 0;
    this.splinters.frustumCulled = false;
    this.splinters.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.group.add(this.dust, this.marks, this.splinters);
  }

  /** Glasses the board: black facets, glassed woods, spires in the waste, a cold violet dusk. Call once. */
  apply(scene: THREE.Scene, terrain: TerrainBuild, sky: ObsidianSky | null, extra: THREE.Object3D[]): void {
    const tiles = terrain.mesh;
    glassify(tiles.geometry, GLASS.tileDark, GLASS.tileLight, 0.03, 0.2);
    tiles.material = obsidianMaterial(tiles.material as THREE.MeshStandardMaterial, this.groundUniforms);
    const c = new THREE.Color();
    const glassed = new Map<THREE.Material, THREE.Material>();
    const glass = (o: THREE.Object3D): void => {
      const im = o as THREE.InstancedMesh;
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      if (o.name === 'outskirts-ground') {
        glassify(mesh.geometry, GLASS.groundDark, GLASS.groundLight, 0.008, 0.11);
        mesh.material = obsidianMaterial(mesh.material as THREE.MeshStandardMaterial, this.groundUniforms);
        return;
      }
      if (o.name === 'roads') {
        glassify(mesh.geometry, GLASS.roadDark, GLASS.roadLight, 0.02, 0.14);
        return;
      }
      const mat = mesh.material as THREE.MeshStandardMaterial;
      if (!mat?.isMeshStandardMaterial) return;
      // Lit things (the fog's crystals) turn to cold violet glints.
      if (mat.emissive && mat.emissive.getHex() !== 0) {
        mesh.material = new THREE.MeshStandardMaterial({ color: '#d9ccff', emissive: '#7a4dff', emissiveIntensity: 1.4, roughness: 0.25, flatShading: true });
        return;
      }
      if (im.isInstancedMesh && im.instanceColor) {
        // Woods, boulders and grass glassed over: near-black with a cold sheen, the light and shade kept.
        for (let i = 0; i < im.count; i++) {
          im.getColorAt(i, c);
          const l = lum(c.r, c.g, c.b);
          c.copy(GLASS.glassy).lerp(GLASS.sheen, clamp01(l / 0.09) * 0.55);
          im.setColorAt(i, c);
        }
        im.instanceColor.needsUpdate = true;
      }
      if (mat.vertexColors || mat.map) return;
      let m2 = glassed.get(mat);
      if (!m2) {
        const m = mat.clone();
        if (!(im.isInstancedMesh && im.instanceColor)) m.color.lerp(GLASS.glassy, 0.8);
        m.roughness = 0.18;
        m.metalness = 0.35;
        m.flatShading = true;
        m2 = m;
        glassed.set(mat, m);
      }
      mesh.material = m2;
    };
    terrain.decor.traverse(glass);
    for (const o of extra) o.traverse(glass);
    this.buildSpires(terrain);
    // A cold dusk: violet-black sky and fog, a pale key and a violet rim.
    scene.background = new THREE.Color('#0a0910');
    if (scene.fog instanceof THREE.Fog) scene.fog.color.set('#16131f');
    terrain.fog?.tint({ low: '#2b2738', mid: '#1c1927', far: '#0a0910', alpha: 1 });
    if (sky) {
      sky.hemi.color.set('#9a9cc0');
      sky.hemi.groundColor.set('#160c22');
      sky.hemi.intensity *= 0.75;
      sky.key.color.set('#e4e6ff');
      sky.key.intensity *= 0.95;
      sky.rim.color.set('#9a74ff');
      sky.rim.intensity *= 1.1;
    }
  }

  /** Spires and clusters of black glass out in the waste, never on a road (one instanced draw). */
  private buildSpires(terrain: TerrainBuild): void {
    const n = this.low ? 70 : 150;
    let k = 0x3c6ef372;
    const rnd = (): number => {
      k = (Math.imul(k ^ (k >>> 13), 1597334677) + 0x7f4a7c15) >>> 0;
      return k / 4294967296;
    };
    const inner = (this.map.bastion + 1.6) * WORLD_PER_HEX;
    const outer = (this.map.radius + 3) * WORLD_PER_HEX;
    const mats: THREE.Matrix4[] = [];
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    const ground = terrain.ground ?? this.ground;
    for (let tries = 0; tries < n * 6 && mats.length < n; tries++) {
      const a = rnd() * Math.PI * 2;
      const d = inner + Math.sqrt(rnd()) * (outer - inner);
      const x = Math.cos(a) * d, z = Math.sin(a) * d;
      if ((terrain.roadDist?.(x, z) ?? 9) < 1.1) continue;
      const far = clamp01((d - inner) / (outer - inner));
      const s = (0.45 + rnd() * 0.6) * (0.8 + far * 1.4);
      e.set((rnd() - 0.5) * 0.35, rnd() * Math.PI * 2, (rnd() - 0.5) * 0.35);
      q.setFromEuler(e);
      mats.push(new THREE.Matrix4().compose(new THREE.Vector3(x, ground(x, z) - 0.05, z), q, new THREE.Vector3(s, s * (0.9 + rnd() * 0.9), s)));
    }
    if (mats.length === 0) return;
    const im = new THREE.InstancedMesh(
      spireGeo(),
      new THREE.MeshStandardMaterial({ color: '#0c0a12', emissive: '#1c1030', emissiveIntensity: 0.6, roughness: 0.08, metalness: 0.45, flatShading: true }),
      mats.length,
    );
    const col = new THREE.Color();
    mats.forEach((m, i) => {
      im.setMatrixAt(i, m);
      im.setColorAt(i, col.setHSL(0.72 + rnd() * 0.06, 0.15, 0.55 + rnd() * 0.45));
    });
    im.instanceMatrix.needsUpdate = true;
    im.castShadow = false;
    im.name = 'obsidian-spires';
    this.spires = im;
    this.group.add(im);
  }

  /** Draws the zones of a sim tick: cracking glass, pits and shards. */
  /** Shows or hides the glass dust in the air (the deep crypt keeps them to their own floor). */
  ambient(on: boolean): void {
    this.dust.visible = on;
  }

  zones(zones: readonly Zone[]): void {
    const list: Zone[] = [];
    for (const z of zones) if ((z.kind === 'glass' || z.kind === 'shards') && list.length < MAX_MARKS) list.push(z);
    let key = '';
    let sk = '';
    for (const z of list) {
      key += `${z.id},`;
      if (z.kind === 'shards' || (z.warn ?? 0) < 0) sk += `${z.id}${z.kind === 'shards' ? 's' : 'p'},`;
    }
    this.list = list;
    if (key !== this.markKey) {
      this.markKey = key;
      this.buildMarks(list);
    }
    if (sk !== this.splinterKey) {
      this.splinterKey = sk;
      this.buildSplinters(list);
    }
    const arr = this.markState.array as Float32Array;
    list.forEach((z, i) => {
      const max = Math.max(this.warnMax.get(z.id) ?? 0, z.warn ?? 0, 1);
      this.warnMax.set(z.id, max);
      const st = glassState(z, max);
      for (let v = 0; v < VERTS; v++) arr[(i * VERTS + v) * 2 + 1] = st;
    });
    this.markState.needsUpdate = true;
    if (this.warnMax.size > 96) for (const id of [...this.warnMax.keys()]) if (!list.some((z) => z.id === id)) this.warnMax.delete(id);
  }

  /** How far a crack's countdown has run (0 → 1); 1 for a pit. */
  progress(z: Zone): number {
    const w = z.warn ?? 0;
    if (w <= 0) return 1;
    const max = Math.max(this.warnMax.get(z.id) ?? 0, w, 1);
    return clamp01(1 - (w - 1) / max);
  }

  private drapeOf(z: Zone, reach: number, lift: number): Draped {
    let d = this.drapes.get(z.id);
    if (!d) {
      d = drape(z.x * K, z.y * K, (z.radius / 1000) * WORLD_PER_HEX, reach, this.ground, lift);
      this.drapes.set(z.id, d);
    }
    return d;
  }

  private buildMarks(list: Zone[]): void {
    const geo = this.marks.geometry;
    if (list.length === 0) {
      this.marks.visible = false;
      return;
    }
    const pos = geo.getAttribute('position') as THREE.BufferAttribute;
    const local = geo.getAttribute('aLocal') as THREE.BufferAttribute;
    const parr = pos.array as Float32Array;
    const larr = local.array as Float32Array;
    const sarr = this.markState.array as Float32Array;
    const index: number[] = [];
    list.forEach((z, i) => {
      const d = this.drapeOf(z, z.kind === 'shards' ? 1.05 : 1.2, 0.075);
      parr.set(d.pos, i * VERTS * 3);
      larr.set(d.local, i * VERTS * 2);
      const s = ((z.id * 0.7548776) % 1) * 5;
      for (let v = 0; v < VERTS; v++) sarr[(i * VERTS + v) * 2] = s;
      for (const ix of d.index) index.push(ix + i * VERTS);
    });
    pos.needsUpdate = true;
    local.needsUpdate = true;
    geo.setIndex(index);
    this.marks.visible = true;
    if (this.drapes.size > 96) for (const id of [...this.drapes.keys()]) if (!list.some((z) => z.id === id)) this.drapes.delete(id);
  }

  /** Splinters standing up: a handful in each patch of shards, a ring of them on the rim of each pit. */
  private buildSplinters(list: Zone[]): void {
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const e = new THREE.Euler();
    const v = new THREE.Vector3();
    const sc = new THREE.Vector3();
    let n = 0;
    for (const z of list) {
      const pit = z.kind === 'glass';
      if (pit && (z.warn ?? 0) >= 0) continue;
      let k = (z.id * 2654435761) >>> 0;
      const rnd = (): number => {
        k = (Math.imul(k ^ (k >>> 15), 2246822519) + 0x6d2b79f5) >>> 0;
        return k / 4294967296;
      };
      const r = (z.radius / 1000) * WORLD_PER_HEX;
      const count = pit ? (this.low ? 7 : 11) : this.low ? 4 : 8;
      for (let i = 0; i < count && n < MAX_SPLINTERS; i++) {
        const a = pit ? (i / count) * Math.PI * 2 + rnd() * 0.4 : rnd() * Math.PI * 2;
        const d = pit ? r * (0.72 + rnd() * 0.12) : Math.sqrt(rnd()) * r * 0.8;
        const x = z.x * K + Math.cos(a) * d, zz = z.y * K + Math.sin(a) * d;
        // Pit splinters lean out of the hole; shards lie every which way.
        const lean = pit ? 0.5 + rnd() * 0.4 : 0.3 + rnd() * 1.0;
        e.set(Math.sin(a) * lean, rnd() * Math.PI * 2, -Math.cos(a) * lean);
        q.setFromEuler(e);
        const s = pit ? 0.8 + rnd() * 0.7 : 0.5 + rnd() * 0.6;
        m.compose(v.set(x, this.ground(x, zz) + 0.02, zz), q, sc.set(s, s * (0.8 + rnd() * 0.6), s));
        this.splinters.setMatrixAt(n++, m);
      }
    }
    this.splinters.count = n;
    this.splinters.instanceMatrix.needsUpdate = true;
  }

  /** Every animation frame: the glints run along the seams, the dust turns, the cracks blink. */
  frame(now: number): void {
    this.uniforms.uTime.value = now % 3600;
  }

  /** The glass layer currently drawn (for the view's sparks): the zones of the last sync. */
  get current(): readonly Zone[] {
    return this.list;
  }

  /** Draw calls this layer adds right now (for measuring). */
  draws(): number {
    return 1 + (this.marks.visible ? 1 : 0) + (this.splinters.count > 0 ? 1 : 0) + (this.spires ? 1 : 0);
  }

  dispose(): void {
    this.dust.geometry.dispose();
    (this.dust.material as THREE.Material).dispose();
    this.marks.geometry.dispose();
    (this.marks.material as THREE.Material).dispose();
    this.splinters.geometry.dispose();
    (this.splinters.material as THREE.Material).dispose();
    this.spires?.geometry.dispose();
    (this.spires?.material as THREE.Material | undefined)?.dispose();
  }
}

/** Obsidiana colours for the view's bursts. */
export const OBSIDIAN = {
  glint: new THREE.Color('#d9ccff'),
  violet: new THREE.Color('#8a5cff'),
  white: new THREE.Color('#f4f0ff'),
  red: new THREE.Color('#ff4f3a'),
  heart: new THREE.Color('#ff3d4f'),
};
